import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { EventType, SectionName } from "@/global/store/data/types";

export function findMappedSection(
  teamConfig: TeamConfig,
  apiSection: SectionName
) {
  const mapping = teamConfig?.seatMapConfigs?.sectionNameMapping;
  if (!mapping) return apiSection;

  let sectionName = { ...apiSection };

  Object.keys(mapping).forEach((sectionId) => {
    const mappingArr = mapping[sectionId];
    if (mappingArr?.includes(apiSection?.mapSectionName)) {
      sectionName.mapSectionName = sectionId;
      sectionName.sectionNames = mappingArr;
    }
  });
  return sectionName;
}

export function toggleViewerNodeSelection(viewer: any, node?: any) {
  if (!viewer || !node || node?.state !== "available") return;
  // all selected nodes
  let selectedNodes = viewer?.getNodesByTag("section", "selected");
  let isSelected = selectedNodes?.map((itm: any) => itm.id).includes(node.id);
  if (isSelected) return viewer?.setNodesTag(node, null);

  // setting up new selection
  viewer?.setNodesTag(node, "selected");
  viewer?.select(node);

  // cleaning up existing selection
  viewer?.setNodesTag(selectedNodes, null);
}

export function findSeatMapSectionIdFromMapping(
  mapSectionName: string,
  teamConfig: TeamConfig
) {
  const mapping = teamConfig?.seatMapConfigs?.sectionNameMapping;
  if (!mapping) return mapSectionName;

  let sectionId = mapSectionName;

  Object.keys(mapping).forEach((secId) => {
    const mappingArr = mapping[secId];
    if (mappingArr?.includes(mapSectionName)) {
      sectionId = secId;
    }
  });

  return sectionId;
}

export interface SeatMap {
  venueId: string;
  mapId: string;
}

/**
 * Calculates the seat map id based on the active event
 * @param teamConfig TeamConfig
 * @param activeEvent
 * @returns SeatMap
 */
// prettier-ignore
export function calcDynamicSeatMapId(teamConfig: TeamConfig, activeEvent: EventType | null): SeatMap {
  if (teamConfig?.seatMapConfigs?.dynamicSeatMapIds && activeEvent?.categories) {
    const dynamicSeatMap = teamConfig?.seatMapConfigs?.dynamicSeatMapIds?.find(
      (seatMap) => seatMap.category.toLowerCase() === activeEvent?.categories?.toLowerCase()
    );

    if (dynamicSeatMap) {
      return {
        venueId: dynamicSeatMap?.venueId || teamConfig?.seatMapConfigs?.venueId,
        mapId: dynamicSeatMap?.mapId || teamConfig?.seatMapConfigs?.mapId,
      };
    }
  } else {
    return {
      venueId: teamConfig?.seatMapConfigs?.venueId || "",
      mapId: teamConfig?.seatMapConfigs?.mapId || "",
    };
  }

  return {
    venueId: "",
    mapId: "",
  };
}

/**
 * Calculates the dynamic seat map static image based on the active event
 * @param teamConfig TeamConfig
 * @param activeEvent
 * @returns string
 */
export function calcDynamicSeatMapStaticImage(
  teamConfig: TeamConfig,
  activeEvent: EventType | null
): string {
  if (
    teamConfig?.seatMapConfigs?.dynamicSeatMapStaticImages &&
    activeEvent?.categories
  ) {
    const categoryMatches =
      teamConfig?.seatMapConfigs?.dynamicSeatMapStaticImages?.filter(
        (seatMap) =>
          seatMap.category.toLowerCase() ===
          activeEvent?.categories?.toLowerCase()
      );

    // prioritize entry that matches both category and eventCode
    if (activeEvent?.eventCode) {
      const eventCodeMatch = categoryMatches.find(
        (seatMap) =>
          seatMap.eventCodes?.includes(activeEvent.eventCode),
      );
      if (eventCodeMatch) {
        return eventCodeMatch.staticSeatMapImage || "";
      }
    }

    // fallback to category-only match (entry without eventCodes)
    const categoryOnlyMatch = categoryMatches.find(
      (seatMap) => !seatMap.eventCodes,
    );
    if (categoryOnlyMatch) {
      return categoryOnlyMatch.staticSeatMapImage || "";
    }

    // fallback to first category match
    if (categoryMatches.length > 0) {
      return categoryMatches[0].staticSeatMapImage || "";
    }
  }

  return teamConfig?.seatMapConfigs?.staticSeatMapImage || "";
}
